import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { CompetitionDTO, CompetitionResultDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useCompetitionApi } from '../../hooks/useCompetitionApi';

interface CompetitionResultFormProps {
  initialValues: CompetitionResultDTO;
  onSubmit: (data: CompetitionResultDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const CompetitionResultForm: React.FC<CompetitionResultFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CompetitionResultDTO>(initialValues);

  const validationRules: ValidationRules = {
    competitionId: [{ required: true, message: 'Competition is required' }],
  };

  const { errors, validate } = useFormValidation<CompetitionResultDTO>(validationRules);

  const [competitions, setCompetitions] = useState<CompetitionDTO[]>([]);
  const { search: searchCompetitions, isLoading: isLoadingCompetitions } = useCompetitionApi();

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const response = await searchCompetitions({
          filters: {},
          page: 1,
          pageSize: 9999,
          sortBy: 'StartDate',
          sortDescending: true,
        });
        setCompetitions(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching competitions:', error);
      }
    };

    fetchCompetitions();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection id={FORM_SECTIONS.basicInfo.id} title={FORM_SECTIONS.basicInfo.title}>
        <FormField
          id="competitionId"
          label="Competition"
          type="dropdown"
          value={formData.competitionId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Competition"
          required
          error={errors.competitionId}
          options={competitions.map((c) => ({
            value: c.id,
            label: c.name || '',
          }))}
          isLoading={isLoadingCompetitions}
        />
      </FormSection>
    </Form>
  );
};

export default CompetitionResultForm;
